import type { FeatureFlagContribution } from '../shell/contributions';

export type PosthogFlagPayload =
  | string
  | number
  | boolean
  | null
  | undefined
  | { readonly [key: string]: PosthogFlagPayload }
  | readonly PosthogFlagPayload[];

export interface PosthogFlagReader {
  useFeatureFlagEnabled: (key: string) => boolean | undefined;
  isFeatureEnabled: (key: string) => boolean | undefined;
  useFeatureFlagVariantKey: (key: string) => string | boolean | undefined;
  getFeatureFlag: (key: string) => string | boolean | undefined;
  useFeatureFlagPayload: (key: string) => PosthogFlagPayload;
  getFeatureFlagPayload: (key: string) => PosthogFlagPayload;
}

export type PosthogFlagDescriptor = FeatureFlagContribution & {
  readonly provider: 'posthog';
};

const UNCONFIGURED: PosthogFlagReader = {
  useFeatureFlagEnabled: () => undefined,
  isFeatureEnabled: () => undefined,
  useFeatureFlagVariantKey: () => undefined,
  getFeatureFlag: () => undefined,
  useFeatureFlagPayload: () => undefined,
  getFeatureFlagPayload: () => undefined,
};

let reader: PosthogFlagReader = UNCONFIGURED;

export const configurePosthogFlags = (next: PosthogFlagReader): void => {
  reader = next;
};

export const usePosthogFlag = (flag: PosthogFlagDescriptor): boolean | undefined =>
  reader.useFeatureFlagEnabled(flag.key);

export const readPosthogFlag = (flag: PosthogFlagDescriptor): boolean | undefined =>
  reader.isFeatureEnabled(flag.key);

export const usePosthogVariant = (
  flag: PosthogFlagDescriptor
): string | boolean | undefined => reader.useFeatureFlagVariantKey(flag.key);

export const readPosthogVariant = (
  flag: PosthogFlagDescriptor
): string | boolean | undefined => reader.getFeatureFlag(flag.key);

export const usePosthogPayload = (flag: PosthogFlagDescriptor): PosthogFlagPayload =>
  reader.useFeatureFlagPayload(flag.key);

export const readPosthogPayload = (flag: PosthogFlagDescriptor): PosthogFlagPayload =>
  reader.getFeatureFlagPayload(flag.key);

export const __resetPosthogFlagsForTests = (): void => {
  reader = UNCONFIGURED;
};
